/**
 * Create Stripe Checkout Session
 *
 * Creates a Checkout Session and returns the URL for redirection.
 * The user will be redirected to Stripe's hosted checkout page.
 */

export interface CreateCheckoutSessionRequest {
  productId: string;
  successUrl: string;
  cancelUrl: string;
  promotionCodeId?: string;
}

export interface CreateCheckoutSessionResponse {
  success: boolean;
  sessionId?: string;
  url?: string;
  message?: string;
}

export const createCheckoutSession = async (
  accessToken: string,
  productId: string,
  successUrl: string,
  cancelUrl: string,
  promotionCodeId?: string
): Promise<CreateCheckoutSessionResponse> => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/payments/create-checkout-session`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      productId,
      successUrl,
      cancelUrl,
      promotionCodeId,
    }),
    credentials: "include",
  });

  const data = (await response.json().catch(() => ({}))) as { message?: string; sessionId?: string; url?: string; success?: boolean };

  if (!response.ok) {
    const msg = Array.isArray(data?.message) ? data.message.join(" ") : data?.message;
    return {
      success: false,
      message: (typeof msg === "string" && msg) || "Failed to create checkout session",
    };
  }

  return {
    success: true,
    sessionId: data.sessionId as string,
    url: data.url as string,
    message: data.message,
  };
};
